import React, { useRef, useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Play } from 'lucide-react';
import AudioVisualizer from './AudioVisualizer';
import {
  BROWSER_LOCAL_RECOVERY_SNAPSHOT_INTERVAL_MS,
  BrowserLocalRecordingRecovery,
  BrowserLocalRecordingType,
  defaultBrowserLocalRecordingRecovery,
} from '../utils/browserLocalRecordingRecovery';
import { concatenateRecordingBlobs } from '../utils/concatenateRecordingBlobs';
import { INTERVIEW_LANGUAGE_OPTIONS, InterviewLanguage } from '../utils/uploadAudio';
import { formatElapsedClock } from '../utils/formatRecordingDuration';
import { measureAudioDurationMs } from '../utils/measureAudioDuration';

const AUDIO_PREFLIGHT_SAMPLE_COUNT = 40;
const AUDIO_PREFLIGHT_SAMPLE_INTERVAL_MS = 100;

const wait = (milliseconds: number) => new Promise(resolve => setTimeout(resolve, milliseconds));

const getAudioTracks = (stream: MediaStream): MediaStreamTrack[] => {
  if (typeof stream.getAudioTracks === 'function') {
    return stream.getAudioTracks();
  }

  return stream.getTracks().filter(track => track.kind === 'audio');
};

const hasEndedOrMutedTrack = (stream: MediaStream): boolean => {
  const tracks = getAudioTracks(stream);
  return tracks.length === 0 || tracks.some(track => track.readyState === 'ended' || track.muted);
};

const hasUsableMicrophoneSignal = async (
  stream: MediaStream,
  onProgress?: (progressPercent: number) => void
): Promise<boolean> => {
  if (hasEndedOrMutedTrack(stream)) {
    return false;
  }

  const AudioContextConstructor = window.AudioContext || (window as any).webkitAudioContext;
  if (!AudioContextConstructor) {
    return true;
  }

  let audioContext: AudioContext | null = null;
  let source: MediaStreamAudioSourceNode | null = null;

  try {
    audioContext = new AudioContextConstructor();
    source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    source.connect(analyser);

    const data = new Uint8Array(analyser.fftSize);
    for (let sampleIndex = 0; sampleIndex < AUDIO_PREFLIGHT_SAMPLE_COUNT; sampleIndex++) {
      onProgress?.((sampleIndex / AUDIO_PREFLIGHT_SAMPLE_COUNT) * 100);

      if (hasEndedOrMutedTrack(stream)) {
        return false;
      }

      analyser.getByteTimeDomainData(data);
      let min = data[0];
      let max = data[0];
      for (let index = 1; index < data.length; index++) {
        min = Math.min(min, data[index]);
        max = Math.max(max, data[index]);
      }

      if (max !== min) {
        return true;
      }

      await wait(AUDIO_PREFLIGHT_SAMPLE_INTERVAL_MS);
    }

    onProgress?.(100);
    return false;
  } catch (error) {
    console.warn('Could not complete microphone preflight:', error);
    return true;
  } finally {
    source?.disconnect();
    await audioContext?.close();
  }
};

const isUnavailableDeviceError = (error: unknown): boolean => {
  if (!(error instanceof DOMException)) {
    return false;
  }

  return ['NotFoundError', 'OverconstrainedError', 'DevicesNotFoundError'].includes(error.name);
};

interface AudioRecorderProps {
  conversationId: string;
  onRecordingComplete: (audioBlob: Blob, lengthOfRecording: number) => void;
  onReset: () => void;
  selectedThemesCount?: number;
  onSubmit: () => void;
  audioBlob: Blob | null;
  isSubmitting: boolean;
  isTranscribing?: boolean;
  transcript?: string | null;
  anonymizedTranscript?: string | null;
  showAnonymized?: boolean;
  onToggleAnonymized?: (value: boolean) => void;
  transcriptionError?: string | null;
  autoStart?: boolean;
  onRecordingStart?: () => void;
  setHeaderMessage: (value: string) => void;
  recordingType?: BrowserLocalRecordingType;
  interviewLanguage?: InterviewLanguage;
  // Supplying a change handler is what opts a recorder into showing the
  // language dropdown; voice-over recordings inherit the interview language.
  onInterviewLanguageChange?: (language: InterviewLanguage) => void;
  isLanguageSelectionDisabled?: boolean;
  recordingRecovery?: BrowserLocalRecordingRecovery;
  leadingChunkBlob?: Blob | null;
  // Audio already captured in the take being continued. Without it the timer
  // restarts at zero and the uploaded duration undercounts what was recorded.
  leadingDurationMs?: number;
  concatenateBlobs?: (blobs: Blob[]) => Promise<Blob>;
}

const AudioRecorder: React.FC<AudioRecorderProps> = ({
  conversationId,
  onRecordingComplete,
  onReset,
  selectedThemesCount,
  onSubmit,
  audioBlob,
  isSubmitting,
  isTranscribing = false,
  transcript = null,
  anonymizedTranscript = null,
  showAnonymized = false,
  onToggleAnonymized,
  transcriptionError = null,
  autoStart = false,
  onRecordingStart,
  setHeaderMessage,
  recordingType = 'conversation',
  interviewLanguage,
  onInterviewLanguageChange,
  isLanguageSelectionDisabled = false,
  recordingRecovery = defaultBrowserLocalRecordingRecovery,
  leadingChunkBlob = null,
  leadingDurationMs,
  concatenateBlobs = concatenateRecordingBlobs
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  // Audio captured so far, pauses excluded. Shown live so the user can see the
  // recording is running and how much is already banked.
  const [recordedMs, setRecordedMs] = useState(0);
  const [audioStream, setAudioStream] = useState<MediaStream | null>(null);
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');
  const [isPreflighting, setIsPreflighting] = useState(false);
  const [preflightProgress, setPreflightProgress] = useState(0);
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const hasAutoStarted = useRef(false);
  const recordingStartTimeRef = useRef<number>(0);
  // Pausing releases the microphone, so it ends the current recording session
  // exactly like closing the tab does. Each completed segment is kept here and
  // the whole set is joined into one file when the user finishes.
  const pausedSegmentsRef = useRef<Blob[]>([]);
  // Wall-clock time would count the pause itself as recorded audio, which would
  // inflate the processing-time estimate, so segment durations are summed.
  const recordedMsRef = useRef<number>(0);
  const stopReasonRef = useRef<'finish' | 'pause'>('finish');

  useEffect(() => {
    if (!leadingChunkBlob) return;

    let isMounted = true;

    // Prefer the audio's real length over any stored figure: a take carried
    // across page loads is longer than this component's clock can know.
    const seed = (durationMs: number) => {
      if (!isMounted) return;
      recordedMsRef.current = durationMs;
      setRecordedMs(durationMs);
    };

    if (typeof leadingDurationMs === 'number') {
      seed(leadingDurationMs);
    }

    measureAudioDurationMs(leadingChunkBlob).then((measured) => {
      if (measured !== null) {
        seed(measured);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [leadingChunkBlob, leadingDurationMs]);

  useEffect(() => {
    if (!isRecording) return;

    const tick = () => setRecordedMs(
      recordedMsRef.current + (Date.now() - recordingStartTimeRef.current)
    );

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [isRecording]);

  // Get available audio devices
  useEffect(() => {
    const getDevices = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter(device => device.kind === 'audioinput');
        setAudioDevices(audioInputs);

        const savedDeviceId = localStorage.getItem('preferredAudioDevice');
        const savedDevice = audioInputs.find(device => device.deviceId === savedDeviceId);
        const selectedDevice = audioInputs.find(device => device.deviceId === selectedDeviceId);

        if (savedDeviceId && !savedDevice) {
          localStorage.removeItem('preferredAudioDevice');
        }

        if (selectedDeviceId && !selectedDevice) {
          setSelectedDeviceId('');
        }
        
        // Set default device with smart selection and memory
        if (audioInputs.length > 0 && !selectedDevice) {
          // Try to restore previously selected device
          if (savedDevice) {
            setSelectedDeviceId(savedDevice.deviceId);
            return;
          }
          
          // Try to find the best default device
          let defaultDevice = audioInputs[0];
          
          // Priority order: built-in microphone, then external, then others
          const defaultMic = audioInputs.find(device => 
            device.label.toLowerCase().includes('default')
          );

          const builtInMic = audioInputs.find(device => 
            device.label.toLowerCase().includes('built-in') || 
            device.label.toLowerCase().includes('internal')
          );
          
          const externalMic = audioInputs.find(device => 
            device.label.toLowerCase().includes('external') || 
            device.label.toLowerCase().includes('usb') ||
            device.label.toLowerCase().includes('bluetooth')
          );
          
          if (defaultMic) {
            defaultDevice = defaultMic;
          } else if (builtInMic) {
            defaultDevice = builtInMic;
          } else if (externalMic) {
            defaultDevice = externalMic;
          }
          
          setSelectedDeviceId(defaultDevice.deviceId);
        }
      } catch (error) {
        console.error('Error getting devices:', error);
      }
    };

    getDevices();

    // Listen for device changes
    navigator.mediaDevices.addEventListener('devicechange', getDevices);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', getDevices);
    };
  }, [selectedDeviceId]);

  // Save device preference when user changes selection
  const handleDeviceChange = (deviceId: string) => {
    setSelectedDeviceId(deviceId);
    localStorage.setItem('preferredAudioDevice', deviceId);
  };

  // Auto-start recording if autoStart is true
  useEffect(() => {
    if (autoStart && selectedDeviceId && !hasAutoStarted.current && !isRecording && !audioBlob) {
      hasAutoStarted.current = true;
      // Small delay to ensure UI is ready
      setTimeout(() => {
        startRecording();
      }, 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, selectedDeviceId, isRecording, audioBlob]);

  const startRecording = async ({ isResume = false }: { isResume?: boolean } = {}) => {
    setIsPreflighting(true);
    setPreflightProgress(0);

    try {
      const constraints = {
        audio: {
          deviceId: selectedDeviceId ? { exact: selectedDeviceId } : undefined
        }
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      const hasUsableSignal = await hasUsableMicrophoneSignal(stream, setPreflightProgress);
      if (!hasUsableSignal) {
        stream.getTracks().forEach(track => track.stop());
        toast.error('De opname is niet gestart: er is geen bruikbaar microfoonsignaal gedetecteerd. Controleer of de microfoon niet gedempt is.');
        return;
      }

      setPreflightProgress(100);
      setAudioStream(stream);
      
      // Try to use MP3 format, but fall back to WAV if not supported
      let mediaRecorder: MediaRecorder;
      try {
        mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/mp3' });
      } catch (e) {
        try {
          mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/mpeg' });
        } catch (e2) {
          try {
            mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/wav' });
          } catch (e3) {
            // MP3 and WAV not supported, using default format
            mediaRecorder = new MediaRecorder(stream);
          }
        }
      }
      
      mediaRecorderRef.current = mediaRecorder;
      // Each recording is its own session: store only this session's audio and
      // never glue the recovered take into the new container. The recovered
      // take stays a separate session and is joined later by decoding to PCM.
      audioChunksRef.current = [];
      recordingStartTimeRef.current = Date.now();
      const startedAt = new Date(recordingStartTimeRef.current).toISOString();
      const sessionId = startedAt;
      mediaRecorder.start(BROWSER_LOCAL_RECOVERY_SNAPSHOT_INTERVAL_MS);

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
          const mimeType = mediaRecorder.mimeType || event.data.type || 'audio/mp3';
          // Persist this session's recording from its start until now as one
          // blob, replacing the previous snapshot, so recovery never has to
          // stitch incremental chunks back together within a session.
          recordingRecovery.saveSnapshot({
            conversationId,
            recordingType,
            mimeType,
            startedAt,
            sessionId,
            blob: new Blob(audioChunksRef.current, { type: mimeType }),
            interviewLanguage,
            // Segments already banked plus this session so far, so the case
            // list can report how much audio is waiting without decoding it.
            durationMs: recordedMsRef.current + (Date.now() - recordingStartTimeRef.current),
          });
        }
      };

      mediaRecorder.onstop = async () => {
        const wasPause = stopReasonRef.current === 'pause';
        stopReasonRef.current = 'finish';

        // Releasing the microphone is what makes a pause visible to the client:
        // the browser's recording indicator goes out.
        const releaseMicrophone = () => {
          stream.getTracks().forEach(track => track.stop());
          setAudioStream(null);
        };

        if (audioChunksRef.current.length === 0) {
          console.error('No audio data collected');
          toast.error('Geen audio opgenomen. Probeer het opnieuw.');
          releaseMicrophone();
          return;
        }

        try {
          // Determine the correct MIME type based on what was actually used
          const mimeType = mediaRecorder.mimeType || 'audio/mp3';
          const sessionBlob = new Blob(audioChunksRef.current, { type: mimeType });
          recordedMsRef.current += Date.now() - recordingStartTimeRef.current;

          setRecordedMs(recordedMsRef.current);

          if (wasPause) {
            // Hold the segment until the user finishes; the snapshot for it is
            // already in IndexedDB, so closing the tab now recovers everything.
            pausedSegmentsRef.current = [...pausedSegmentsRef.current, sessionBlob];
            releaseMicrophone();
            return;
          }

          // Join every segment by decoding to PCM and re-encoding one valid
          // file, instead of gluing container files together (which would
          // corrupt the stream). A recovered take leads the list.
          const segments = [
            ...(leadingChunkBlob ? [leadingChunkBlob] : []),
            ...pausedSegmentsRef.current,
            sessionBlob,
          ];
          const isJoined = segments.length > 1;
          const audioBlob = isJoined ? await concatenateBlobs(segments) : sessionBlob;

          // Only a joined take needs measuring: for a single session the clock
          // is reliable, and decoding a long recording again is expensive.
          const measuredMs = isJoined ? await measureAudioDurationMs(audioBlob) : null;
          const durationInSeconds = (measuredMs ?? recordedMsRef.current) / 1000;
          pausedSegmentsRef.current = [];
          recordedMsRef.current = 0;
          setRecordedMs(0);
          onRecordingComplete(audioBlob, durationInSeconds);
        } catch (error) {
          console.error('Error creating blob:', error);
          toast.error('Fout bij het aanmaken van audiobestand. Probeer het opnieuw.');
        }

        releaseMicrophone();
      };

      setIsRecording(true);
      setIsPaused(false);
      // Resuming must not report a fresh start: that clears the stored
      // recovery, discarding the segments recorded before the pause.
      if (onRecordingStart && !isResume) {
        onRecordingStart();
      }
    } catch (error) {
      console.error('Error starting recording:', error);
      if (isUnavailableDeviceError(error) && selectedDeviceId) {
        setAudioDevices(devices => devices.filter(device => device.deviceId !== selectedDeviceId));
        setSelectedDeviceId('');
        localStorage.removeItem('preferredAudioDevice');
        toast.error('De geselecteerde microfoon is niet meer beschikbaar. Kies een andere microfoon.');
        return;
      }
      toast.error('Fout bij het starten van de opname. Controleer de microfoontoestemming.');
    } finally {
      setIsPreflighting(false);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      stopReasonRef.current = 'finish';
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      setHeaderMessage('Gesprek gestopt');
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      stopReasonRef.current = 'pause';
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(true);
      setHeaderMessage('Opname gepauzeerd. De microfoon is uit.');
    }
  };

  const resumeRecording = () => {
    setHeaderMessage('Opname hervat.');
    startRecording({ isResume: true });
  };

  const finishPausedRecording = () => {
    // Nothing is recording, so there is no session to stop: hand over the
    // segments captured before the pause.
    if (pausedSegmentsRef.current.length === 0) {
      return;
    }

    const segments = [
      ...(leadingChunkBlob ? [leadingChunkBlob] : []),
      ...pausedSegmentsRef.current,
    ];
    const clockDurationMs = recordedMsRef.current;

    const finish = async () => {
      try {
        const isJoined = segments.length > 1;
        const audioBlob = isJoined ? await concatenateBlobs(segments) : segments[0];
        const measuredMs = isJoined ? await measureAudioDurationMs(audioBlob) : null;
        const durationInSeconds = (measuredMs ?? clockDurationMs) / 1000;
        pausedSegmentsRef.current = [];
        recordedMsRef.current = 0;
        setRecordedMs(0);
        setIsPaused(false);
        onRecordingComplete(audioBlob, durationInSeconds);
      } catch (error) {
        console.error('Error creating blob:', error);
        toast.error('Fout bij het aanmaken van audiobestand. Probeer het opnieuw.');
      }
    };

    finish();
  };

  const handleReset = () => {
    pausedSegmentsRef.current = [];
    recordedMsRef.current = 0;
    setRecordedMs(0);
    setIsPaused(false);
    setAudioStream(null);
    onReset();
  };

  const showDeviceSelector = audioDevices.length > 1;
  const showLanguageSelector = Boolean(onInterviewLanguageChange);

  return (
    <div>
      {!audioBlob ? (
        <div className="space-y-6">
          {/* Audio Visualizer (shows only when recording) */}
          <AudioVisualizer
            isRecording={isRecording}
            stream={audioStream}
            onStopRecording={stopRecording}
            onPauseRecording={pauseRecording}
            elapsedLabel={formatElapsedClock(recordedMs)}
          />

          {isPaused && (
            <div className="flex flex-col items-center">
              <span className="badge-context mb-4 border-brand-red text-brand-red">
                Opname gepauzeerd
              </span>

              <p className="mb-4 text-3xl font-semibold tabular-nums text-brand-blauw">
                {formatElapsedClock(recordedMs)}
              </p>

              {/* Bars are held flat and dimmed: the microphone is released, so
                  animating them would suggest we are still listening. */}
              <div className="flex items-end justify-center gap-2 h-32 opacity-40" aria-hidden="true">
                {Array.from({ length: 8 }, (_, i) => (
                  <div
                    key={i}
                    className="w-3 rounded-full"
                    style={{
                      height: '15%',
                      backgroundColor: i % 2 === 0 ? '#2380AC' : '#E73459',
                    }}
                  />
                ))}
              </div>

              <p className="mt-4 max-w-md text-center text-base text-brand-blauw">
                De microfoon staat uit. Deze opname is bewaard — hervat wanneer
                je verdergaat met het gesprek.
              </p>

              <div className="pt-6 flex flex-wrap items-center justify-center gap-4">
                <button
                  type="button"
                  onClick={resumeRecording}
                  className="btn-gesprekskompas-accent sm:w-auto"
                >
                  <Play className="w-5 h-5" aria-hidden="true" />
                  Hervat opname
                </button>

                <button
                  type="button"
                  onClick={finishPausedRecording}
                  className="btn-gesprekskompas-primary sm:w-auto"
                >
                  Verstuur om te verwerken
                </button>
              </div>
            </div>
          )}

          {/* Recording settings - microphone and conversation language */}
          {!isRecording && !isPaused && (showDeviceSelector || showLanguageSelector) && (
            <div className="flex flex-wrap items-end gap-4">
              {showDeviceSelector && (
                <div>
                  <label htmlFor="audioDevice" className="block text-xs font-medium text-brand-dark-gray mb-1">
                    Microfoon
                  </label>
                  <select
                    id="audioDevice"
                    value={selectedDeviceId}
                    onChange={(e) => handleDeviceChange(e.target.value)}
                    className="select-gesprekskompas w-full max-w-80"
                  >
                    {audioDevices.map((device) => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {device.label || `Microfoon ${device.deviceId.slice(0, 8)}...`}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {showLanguageSelector && (
                <div>
                  <label htmlFor="interviewLanguage" className="block text-xs font-medium text-brand-dark-gray mb-1">
                    Gesprekstaal
                  </label>
                  <select
                    id="interviewLanguage"
                    value={interviewLanguage ?? 'nl'}
                    onChange={(e) => onInterviewLanguageChange?.(e.target.value as InterviewLanguage)}
                    disabled={isLanguageSelectionDisabled}
                    title="Gesprekken met meerdere talen of een tolk kunnen een minder goede transcriptie geven."
                    className="select-gesprekskompas"
                  >
                    {INTERVIEW_LANGUAGE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {showLanguageSelector && !isPaused && interviewLanguage === 'ar' && (
            <p className="max-w-md rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              Arabisch wordt verwerkt als Modern Standaard Arabisch. De samenvatting,
              rapportage en SYS4-uitvoer blijven Nederlands, maar kunnen fouten bevatten.
            </p>
          )}

          {/* Start button, or the pre-flight meter while we wait for audio */}
          {!isRecording && !isPaused && (
            <div className="pt-6">
              {isPreflighting ? (
                <div className="w-full max-w-xs" role="status" aria-live="polite">
                  <p className="mb-3 text-sm font-medium text-brand-black">Wachtend op audio voordat gesprek begint</p>
                  <div
                    role="slider"
                    aria-label="Microfoon preflight voortgang"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(preflightProgress)}
                    className="h-3 w-full overflow-hidden rounded-full bg-brand-primary"
                  >
                    <div
                      className="h-full rounded-full bg-brand-red transition-all duration-100"
                      style={{ width: `${preflightProgress}%` }}
                    />
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => startRecording()}
                  className="btn-roll-effect cursor-pointer"
                >
                  <span className="btn-accent w-full lg:w-auto rounded-xl text-xl lg:px-5 h-12">
                    Start gesprek
                  </span>
                  <span className="btn-roll-circle bg-brand-red" aria-hidden="true">
                    <img src={`${process.env.PUBLIC_URL}/assets/arrow.svg`} alt="" className="w-6" />
                  </span>
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {/* Transcription Card - Only when ready */}
          {transcript && !isTranscribing && (
            <div className="bg-white border border-[#E1D6C6] rounded-xl p-6">
              <p className="text-sm text-brand-black leading-relaxed whitespace-pre-wrap">
                {showAnonymized ? (anonymizedTranscript || transcript) : transcript}
              </p>
            </div>
          )}

          {/* Error Message Detail */}
          {transcriptionError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-700">{transcriptionError}</p>
              <p className="text-xs text-red-600 mt-2">Je kunt de opname opnieuw proberen of doorgaan zonder tekst.</p>
            </div>
          )}

          {/* Action Buttons - only show when not transcribing */}
          {!isTranscribing && (
            <div className="flex gap-3">
              <button
                onClick={onSubmit}
                disabled={isSubmitting}
                className="flex-1 btn-gesprekskompas-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Verwerken...' : 'Verwerk Audio'}
              </button>
              <button
                onClick={handleReset}
                className="px-6 py-3 border border-brand-blauw text-brand-blauw rounded-xl font-medium transition-colors hover:bg-brand-blauw/10"
              >
                Nieuwe Opname
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AudioRecorder;